import { useEffect, useRef } from 'react'

interface UseIntersectionObserverProps {
  onVisibilityChange: (id: string, isVisible: boolean) => void
  threshold?: number
  rootMargin?: string
}

export const useIntersectionObserver = ({
  onVisibilityChange,
  threshold = 0.5,
  rootMargin = '0px',
}: UseIntersectionObserverProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedElementsRef = useRef<Map<string, Element>>(new Map())

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-notification-id')
          if (id) {
            onVisibilityChange(id, entry.isIntersecting)
          }
        })
      },
      {
        threshold,
        rootMargin,
      },
    )

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [onVisibilityChange, threshold, rootMargin])

  const observeElement = (element: Element, id: string) => {
    if (observerRef.current && element) {
      element.setAttribute('data-notification-id', id)
      observerRef.current.observe(element)
      observedElementsRef.current.set(id, element)
    }
  }

  const unobserveElement = (id: string) => {
    const element = observedElementsRef.current.get(id)
    if (observerRef.current && element) {
      observerRef.current.unobserve(element)
      observedElementsRef.current.delete(id)
    }
  }

  return {
    observeElement,
    unobserveElement,
  }
}
